(() => {
  const ROOT_SELECTOR = '.product-card__swatches';
  const SWATCH_SELECTOR = '.color-swatch';
  const MORE_CLASS = 'product-card__swatch-more';
  const HIDDEN_CLASS = 'is-swatch-overflow';
  const observedFieldsets = new WeakSet();

  function getSwatches(fieldset) {
    return [...fieldset.querySelectorAll(SWATCH_SELECTOR)].filter((el) => el.style.display !== 'none');
  }

  function ensureMoreBadge(fieldset) {
    let more = fieldset.querySelector(`.${MORE_CLASS}`);
    if (!more) {
      more = document.createElement('span');
      more.className = MORE_CLASS;
      more.setAttribute('aria-hidden', 'true');
      fieldset.appendChild(more);
    } else if (more.tagName === 'BUTTON') {
      const span = document.createElement('span');
      span.className = MORE_CLASS;
      span.setAttribute('aria-hidden', 'true');
      span.hidden = more.hidden;
      span.textContent = more.textContent;
      more.replaceWith(span);
      more = span;
    }
    return more;
  }

  function fitFieldset(fieldset) {
    if (!fieldset || !fieldset.isConnected) {
      return;
    }

    const more = ensureMoreBadge(fieldset);
    const swatches = getSwatches(fieldset);
    if (!swatches.length) {
      more.hidden = true;
      more.textContent = '';
      return;
    }

    const meta = fieldset.closest('.product-card__meta');
    const priceList = meta?.querySelector('.price-list');
    const metaWidth = meta?.clientWidth ?? fieldset.clientWidth;
    if (metaWidth <= 0) {
      return;
    }

    const priceWidth = priceList?.offsetWidth ?? 0;
    const metaStyles = meta ? getComputedStyle(meta) : null;
    const columnGap = metaStyles ? (parseFloat(metaStyles.columnGap) || 0) : 0;
    const available = Math.max(0, metaWidth - priceWidth - columnGap);
    if (available <= 0) {
      return;
    }

    swatches.forEach((swatch) => swatch.classList.remove(HIDDEN_CLASS));
    more.hidden = true;
    more.textContent = '';

    const fullWidth = swatches.reduce((sum, swatch) => sum + swatch.offsetWidth, 0);
    if (fullWidth <= available + 0.5) {
      return;
    }

    more.hidden = false;
    more.textContent = `+${swatches.length}`;
    const moreWidth = more.offsetWidth;
    const limit = Math.max(0, available - moreWidth);

    let used = 0;
    let visibleCount = 0;
    for (let i = 0; i < swatches.length; i += 1) {
      const width = swatches[i].offsetWidth;
      if (used + width > limit + 0.5) {
        break;
      }
      used += width;
      visibleCount += 1;
    }

    if (visibleCount === 0 && swatches.length > 0) {
      visibleCount = 1;
    }

    while (visibleCount > 1) {
      more.textContent = `+${swatches.length - visibleCount}`;
      const widths = swatches.slice(0, visibleCount).reduce((sum, swatch) => sum + swatch.offsetWidth, 0) + more.offsetWidth;
      if (widths <= available + 0.5) {
        break;
      }
      visibleCount -= 1;
    }

    const hiddenCount = swatches.length - visibleCount;
    swatches.forEach((swatch, index) => {
      swatch.classList.toggle(HIDDEN_CLASS, index >= visibleCount);
    });

    if (hiddenCount > 0) {
      more.hidden = false;
      more.textContent = `+${hiddenCount}`;
    } else {
      more.hidden = true;
      more.textContent = '';
    }
  }

  function fitAll(root = document) {
    root.querySelectorAll(ROOT_SELECTOR).forEach(fitFieldset);
  }

  let resizeTimer = null;
  function onResize() {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => fitAll(), 100);
  }

  function fieldsetFromResizeTarget(target) {
    if (!(target instanceof Element)) {
      return null;
    }
    if (target.matches(ROOT_SELECTOR)) {
      return target;
    }
    return target.querySelector(ROOT_SELECTOR);
  }

  const resizeObserver = typeof ResizeObserver !== 'undefined'
    ? new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const fieldset = fieldsetFromResizeTarget(entry.target);
        if (fieldset) {
          fitFieldset(fieldset);
        }
      });
    })
    : null;

  function observe(fieldset) {
    if (!resizeObserver || observedFieldsets.has(fieldset)) {
      return;
    }
    observedFieldsets.add(fieldset);
    resizeObserver.observe(fieldset);
    const meta = fieldset.closest('.product-card__meta');
    if (meta) {
      resizeObserver.observe(meta);
    }
  }

  function init(root = document) {
    root.querySelectorAll(ROOT_SELECTOR).forEach((fieldset) => {
      observe(fieldset);
      fitFieldset(fieldset);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  if (document.fonts?.ready) {
    document.fonts.ready.then(() => fitAll());
  }

  window.addEventListener('resize', onResize);

  const mutationObserver = new MutationObserver((mutations) => {
    let needsFit = false;
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) {
          return;
        }
        if (node.matches?.(ROOT_SELECTOR) || node.querySelector?.(ROOT_SELECTOR)) {
          needsFit = true;
        }
      });
    });
    if (needsFit) {
      window.requestAnimationFrame(() => init());
    }
  });
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
